import { Head } from '@inertiajs/react'
import { useState } from 'react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DataTable, { Column } from '@/components/DataTable'
import DateRangeFilter, { DateRange } from '@/components/DateRangeFilter'
import { UserPlus, Users, Clock, CheckCircle, XCircle } from 'lucide-react'

interface Referral {
  id: number
  first_name: string
  last_name: string
  full_name: string
  email: string
  status: 'invited' | 'document_signed' | 'past_agent'
  referring_agent_name: string
  referring_agent_id: number
  document_sent_at: string | null
  document_signed_at: string | null
  created_at: string
}

interface SponsoringAgent {
  id: number
  name: string
  email: string
  total_invited: number
  docs_sent: number
  docs_signed: number
}

interface Props extends PageProps {
  referrals: Referral[]
  sponsoring_agents: SponsoringAgent[]
  date_range: DateRange
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
    invited: {
      label: 'Invited',
      className: 'bg-amber-100 text-amber-800',
      icon: <Clock className="h-3.5 w-3.5" />
    },
    document_signed: {
      label: 'Document Signed',
      className: 'bg-green-100 text-green-800',
      icon: <CheckCircle className="h-3.5 w-3.5" />
    },
    past_agent: {
      label: 'Past Agent',
      className: 'bg-gray-100 text-gray-600',
      icon: <XCircle className="h-3.5 w-3.5" />
    }
  }

  const { label, className, icon } = config[status] || config.invited

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${className}`}>
      {icon}
      {label}
    </span>
  )
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

export default function AdminReferralsIndex({ referrals, sponsoring_agents, date_range }: Props) {
  const [activeTab, setActiveTab] = useState<'all' | 'sponsors'>('all')

  const totalInvited = referrals.length
  const totalSigned = referrals.filter(r => r.status === 'document_signed').length
  const totalPending = referrals.filter(r => r.status === 'invited').length

  const referralColumns: Column<Referral>[] = [
    {
      key: 'full_name',
      header: 'Referred Agent',
      sortable: true,
      render: (r) => (
        <div>
          <div className="font-medium text-chestnut-charcoal">{r.full_name}</div>
          <div className="text-xs text-chestnut-charcoal/60">{r.email}</div>
        </div>
      )
    },
    {
      key: 'referring_agent_name',
      header: 'Invited By',
      sortable: true,
      render: (r) => (
        <span className="font-medium text-chestnut-charcoal">{r.referring_agent_name}</span>
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (r) => <StatusBadge status={r.status} />
    },
    {
      key: 'document_sent_at',
      header: 'Doc Sent',
      sortable: true,
      render: (r) => (
        <span className="text-sm text-chestnut-charcoal/80">{formatDate(r.document_sent_at)}</span>
      )
    },
    {
      key: 'document_signed_at',
      header: 'Doc Signed',
      sortable: true,
      render: (r) => (
        <span className="text-sm text-chestnut-charcoal/80">{formatDate(r.document_signed_at)}</span>
      )
    },
    {
      key: 'created_at',
      header: 'Referred On',
      sortable: true,
      render: (r) => (
        <span className="text-sm text-chestnut-charcoal/80">{formatDate(r.created_at)}</span>
      )
    }
  ]

  const sponsorColumns: Column<SponsoringAgent>[] = [
    {
      key: 'name',
      header: 'Sponsoring Agent',
      sortable: true,
      render: (a) => (
        <div>
          <div className="font-medium text-chestnut-charcoal">{a.name}</div>
          <div className="text-xs text-chestnut-charcoal/60">{a.email}</div>
        </div>
      )
    },
    {
      key: 'total_invited',
      header: 'Total Invited',
      sortable: true,
      className: 'text-center',
      render: (a) => (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 font-semibold text-sm">
          {a.total_invited}
        </span>
      )
    },
    {
      key: 'docs_sent',
      header: 'Docs Sent',
      sortable: true,
      className: 'text-center',
      render: (a) => (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-800 font-semibold text-sm">
          {a.docs_sent}
        </span>
      )
    },
    {
      key: 'docs_signed',
      header: 'Docs Signed',
      sortable: true,
      className: 'text-center',
      render: (a) => (
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-800 font-semibold text-sm">
          {a.docs_signed}
        </span>
      )
    }
  ]

  return (
    <AppLayout>
      <Head title="Referrals Management" />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Referrals</h1>
            <p className="text-chestnut-charcoal/70 mt-1">Track all agent referrals and sponsoring agent activity.</p>
          </div>
          <DateRangeFilter dateRange={date_range} path="/admin/referrals" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-100 rounded-lg">
                <UserPlus className="h-5 w-5 text-blue-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{totalInvited}</p>
                <p className="text-xs text-chestnut-charcoal/60">Total Referrals</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{totalPending}</p>
                <p className="text-xs text-chestnut-charcoal/60">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{totalSigned}</p>
                <p className="text-xs text-chestnut-charcoal/60">Signed</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-chestnut-gray">
          <div className="border-b border-chestnut-gray">
            <div className="flex">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'all'
                    ? 'border-chestnut-forest text-chestnut-forest'
                    : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
                }`}
              >
                <span className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  All Referrals ({referrals.length})
                </span>
              </button>
              <button
                onClick={() => setActiveTab('sponsors')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'sponsors'
                    ? 'border-chestnut-forest text-chestnut-forest'
                    : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Sponsoring Agents ({sponsoring_agents.length})
                </span>
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'all' ? (
              <DataTable
                data={referrals}
                columns={referralColumns}
                searchPlaceholder="Search by name, email, or inviter..."
                searchKeys={['full_name', 'email', 'referring_agent_name']}
                pageSize={15}
                emptyMessage="No referrals have been made yet."
                getRowKey={(r) => r.id}
              />
            ) : (
              <DataTable
                data={sponsoring_agents}
                columns={sponsorColumns}
                searchPlaceholder="Search sponsoring agents..."
                searchKeys={['name', 'email']}
                pageSize={15}
                emptyMessage="No agents have made referrals yet."
                getRowKey={(a) => a.id}
                defaultSortKey="total_invited"
                defaultSortDirection="desc"
              />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
